import type { SiteId, SiteState, Slot } from "./types";

// Stripe accepts USD amounts with at most eight digits in the smallest currency
// unit. Outbid only accepts whole-dollar checkouts, so this is the highest whole
// dollar amount that remains within Stripe's 99,999,999-cent ceiling.
export const MAX_CHECKOUT_CENTS = 99_999_900;

export interface SiteConfig {
  id: SiteId;
  domain: string;
  title: string;
  claimNoun: string;
  startCents: number;
  globalLeaderIncrementCents?: number;
  socialTitle: string;
  socialDescription: string;
}

export const SITE_CONFIG: Record<SiteId, SiteConfig> = {
  earth: {
    id: "earth",
    domain: "outbid.earth",
    title: "Put your name on the planet",
    claimNoun: "country",
    startCents: 500,
    globalLeaderIncrementCents: 300,
    socialTitle: "OUTBID EARTH — who owns your country?",
    socialDescription: "Claim a country for your website, take #1, and dare someone to outbid you.",
  },
  shop: {
    id: "shop",
    domain: "outbid.shop",
    title: "The internet's loudest strip mall",
    claimNoun: "shopfront",
    startCents: 300,
    socialTitle: "OUTBID SHOP — the internet's loudest strip mall",
    socialDescription: "Pay once, put your link in the mall, and stay listed forever.",
  },
  homes: {
    id: "homes",
    domain: "outbid.homes",
    title: "Give your URL curb appeal",
    claimNoun: "home",
    startCents: 400,
    socialTitle: "OUTBID HOMES — give your URL curb appeal",
    socialDescription: "Buy your website a home and compete for the best address on the block.",
  },
  land: {
    id: "land",
    domain: "outbid.land",
    title: "Digital dirt. Real bragging rights.",
    claimNoun: "parcel",
    startCents: 200,
    socialTitle: "OUTBID LAND — digital dirt, real bragging rights",
    socialDescription: "Claim a permanent parcel for your website and climb the public ladder.",
  },
};

type PublishedBoard = Pick<SiteState, "slots"> | readonly Slot[];

function isPublishedState(value: PublishedBoard): value is Pick<SiteState, "slots"> {
  return !Array.isArray(value);
}

export function highestPublishedBidCents(stateOrSlots: PublishedBoard): number {
  const slots = isPublishedState(stateOrSlots) ? stateOrSlots.slots : stateOrSlots;
  return slots.reduce(
    (highest, slot) => slot.entries.reduce(
      (slotHighest, entry) => Math.max(slotHighest, entry.amountCents),
      highest,
    ),
    0,
  );
}

export function minimumAcceptedBidCents(state: Pick<SiteState, "site" | "slots">): number {
  const config = SITE_CONFIG[state.site];
  const incrementCents = config.globalLeaderIncrementCents;
  if (incrementCents === undefined) return config.startCents;

  const highestBidCents = highestPublishedBidCents(state);
  return Math.max(
    config.startCents,
    highestBidCents === 0
      ? config.startCents
      : Math.ceil((highestBidCents + incrementCents) / 100) * 100,
  );
}

export function resolveSiteId(): SiteId {
  const injectedSite = document.documentElement.dataset.outbidSite;
  if (injectedSite && ["earth", "shop", "homes", "land"].includes(injectedSite)) {
    return injectedSite as SiteId;
  }

  const hostEnding = window.location.hostname.split(".").at(-1);
  if (hostEnding && ["earth", "shop", "homes", "land"].includes(hostEnding)) {
    return hostEnding as SiteId;
  }

  const firstPath = window.location.pathname.split("/").filter(Boolean)[0];
  if (firstPath && ["earth", "shop", "homes", "land"].includes(firstPath)) {
    return firstPath as SiteId;
  }

  return "earth";
}
